import { OutputData } from '@editorjs/editorjs'
import React, { memo, useMemo } from 'react'

import { EditorJsBenefitParagraphHtml } from '@/_components/universal/editorjs-renderer/components/editorjs-benefit-paragraph-html'
import { sanitizeAndProcessHtml } from '@/_lib/dom-purify.utils'

/**
 * Extracts content from an EditorJS item.
 * Removes leading '+ ' prefix if present and sanitizes HTML.
 *
 * @param item - EditorJS content item
 * @returns Sanitized HTML content string
 */
const getItemContent = (item: any): string => {
	return item?.content?.trim()?.startsWith('+ ')
		? sanitizeAndProcessHtml(item?.content?.trim()?.substring(2), false)
		: sanitizeAndProcessHtml(item?.content, false)
}

/**
 * Checks if an item is marked as "extra" with '+ ' prefix.
 *
 * @param item - EditorJS content item
 * @returns True if item starts with '+ '
 */
const isExtraItem = (item: any): boolean => {
	return item?.content?.trim()?.startsWith('+ ')
}

/**
 * Creates a stable item reference for memoization.
 * Prevents unnecessary re-renders by creating consistent keys.
 *
 * @param item - Original item
 * @param index - Item index
 * @returns Stable item object with consistent key
 */
const createStableItem = (item: any, index: number) => ({
	id: item.id || index,
	content: item.content,
	items: item.items,
	// Create a stable reference by stringifying the content
	stableKey: JSON.stringify({ content: item.content, items: item.items })
})

/**
 * Props for AMBListItem component.
 */
interface IAMBListItemProps {
	/** Content item to render */
	item: any
	/** Whether this is an "extra" item (with special styling) */
	isExtraItem?: boolean
}

/**
 * Renders a single list item with optional nested sub-items.
 * Supports special "extra" styling for items with '+ ' prefix.
 *
 * @param item - List item content
 * @param isExtraItem - Whether to apply "extra" class styling
 */
export const ZAPDetailListItem = memo(({ item, isExtraItem: isExtra }: IAMBListItemProps) => {
	/** Memoized sanitized content to prevent recalculation */
	const sanitizedContent = useMemo(() => {
		return getItemContent(item) || ''
	}, [item])

	/** Memoized sub-items list */
	const subItems = useMemo(() => {
		if (!item.items || item.items.length === 0) return null

		return item.items.map((subItem: any, subIndex: number) => ({
			id: `${item.id}-list-${subIndex}`,
			content: getItemContent(subItem) || ''
		}))
	}, [item.items, item.id])

	return (
		<li className={isExtra ? 'extra' : ''}>
			<span dangerouslySetInnerHTML={{ __html: sanitizedContent }} />
			{subItems && (
				<ul className='list-disc !mt-0'>
					{subItems.map((subItem: { id: string; content: string }) => (
						<li key={subItem.id}>
							<span dangerouslySetInnerHTML={{ __html: subItem.content }} />
						</li>
					))}
				</ul>
			)}
		</li>
	)
})

ZAPDetailListItem.displayName = 'ZAPDetailListItem'

/**
 * Props for MBListItems component.
 */
interface IMBListItemsProps {
	/** Array of list items */
	items: any[]
	/** Block ID for key generation */
	blockId: string
	/** Item index in parent */
	itemIndex: number
	/** Group index for nested lists */
	groupIndex: number
	/** Whether these are "extra" items */
	isExtra: boolean
}

/**
 * Renders a group of list items with consistent styling.
 * Groups items by "extra" status for proper styling.
 *
 * @param items - Array of items to render
 * @param blockId - Parent block ID
 * @param itemIndex - Index in parent
 * @param groupIndex - Group index for keys
 * @param isExtra - Whether this is an "extra" group
 */
export const ZAPDetailListItems = memo(
	({ items, blockId, itemIndex, groupIndex, isExtra }: IMBListItemsProps) => {
		/** Memoized stable items to prevent recreation */
		const stableItems = useMemo(
			() => items.map((item, index) => createStableItem(item, index)),
			[items]
		)

		return (
			<ul className={`list-disc ${isExtra ? 'extra' : ''}`}>
				{stableItems.map((item, listIndex) => (
					<ZAPDetailListItem
						key={`${blockId}-list-${itemIndex}-${groupIndex}-${
							isExtra ? 'extra' : 'standard'
						}-${listIndex}`}
						item={item}
						isExtraItem={isExtra}
					/>
				))}
			</ul>
		)
	}
)

ZAPDetailListItems.displayName = 'ZAPDetailListItems'

/**
 * Props for MBBenefitBlock component.
 */
interface IMBBenefitBlockProps {
	/** EditorJS block data */
	block: any
	/** Benefit ID for key generation */
	benefitId: string | number
	/** Item index in parent */
	itemIndex: number
}

/**
 * Renders a single EditorJS block (paragraph or list).
 * Handles different block types and applies appropriate formatting.
 *
 * @param block - EditorJS block data
 * @param benefitId - Parent benefit ID
 * @param itemIndex - Block index in benefit
 */
export const ZAPDetailBenefitBlock = memo(
	({ block, benefitId, itemIndex }: IMBBenefitBlockProps) => {
		if (block.type === 'paragraph') {
			return (
				<EditorJsBenefitParagraphHtml
					key={`${benefitId}-paragraph-${itemIndex}`}
					html={block.data.text ?? ''}
					className='!mt-0'
				/>
			)
		}

		if (block.type === 'list' || block.type === 'nestedList') {
			/**
			 * Groups list items by type (standard vs extra).
			 * Extra items are those starting with '+ ' prefix.
			 */
			const listItemGroups: { type: 'standard' | 'extra'; items: any[] }[] = []

			for (const item of block.data.items) {
				if (isExtraItem(item)) {
					if (listItemGroups[listItemGroups.length - 1]?.type === 'extra') {
						listItemGroups[listItemGroups.length - 1].items.push(item)
					} else {
						listItemGroups.push({ type: 'extra', items: [item] })
					}
				} else {
					if (listItemGroups[listItemGroups.length - 1]?.type === 'standard') {
						listItemGroups[listItemGroups.length - 1].items.push(item)
					} else {
						listItemGroups.push({ type: 'standard', items: [item] })
					}
				}
			}

			return (
				<React.Fragment key={`${benefitId}-list-${itemIndex}`}>
					{listItemGroups.map((group, groupIndex) => (
						<ZAPDetailListItems
							key={`${benefitId}-list-${itemIndex}-${groupIndex}-${group.type}`}
							items={group.items}
							blockId={block.id}
							itemIndex={itemIndex}
							groupIndex={groupIndex}
							isExtra={group.type === 'extra'}
						/>
					))}
				</React.Fragment>
			)
		}

		return null
	}
)

ZAPDetailBenefitBlock.displayName = 'ZAPDetailBenefitBlock'

/**
 * Props for MBBenefitBlocks component.
 */
interface IMBBenefitBlocksProps {
	/** EditorJS output data */
	data: OutputData
	/** Benefit ID for key generation */
	id: string | number
}

/**
 * Renders all blocks from EditorJS output data.
 * Main renderer component for benefit descriptions.
 *
 * @param data - Complete EditorJS output data
 * @param id - Benefit or program ID
 *
 * @example
 * ```typescript
 * <MBBenefitBlocks data={benefit.description} id={benefit.id} />
 * ```
 */
export const ZAPDetailBenefitBlocks = memo(({ data, id }: IMBBenefitBlocksProps) => {
	/** Memoized blocks with indexes to prevent recreation */
	const blocks = useMemo(
		() =>
			data.blocks.map((block, itemIndex) => ({
				block,
				itemIndex
			})),
		[data.blocks]
	)

	return (
		<div key={`${id}-blocks`}>
			{blocks.map(({ block, itemIndex }) => (
				<ZAPDetailBenefitBlock
					key={`${id}-block-${itemIndex}`}
					block={block}
					benefitId={id}
					itemIndex={itemIndex}
				/>
			))}
		</div>
	)
})

ZAPDetailBenefitBlocks.displayName = 'ZAPDetailBenefitBlocks'
